/**
 * Sentry Apollo Link
 *
 * GraphQLエラーをSentryにキャプチャするためのApollo Client用リンク
 */

import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/nextjs';

import { isSentryEnabled } from '@/lib/sentry/config';

import { isExpectedAuthGraphqlError, isExpectedAuthNetworkError } from './sentry-auth-error-filter';

// 機密情報を含む可能性のあるフィールド名
const SENSITIVE_FIELDS = ['password', 'token', 'email', 'phone', 'secret', 'accessToken', 'idToken'];

/**
 * GraphQL変数から機密情報をサニタイズ
 */
const sanitizeVariables = (variables: Record<string, unknown>): Record<string, unknown> => {
  if (!variables || typeof variables !== 'object') {
    return variables;
  }

  return Object.fromEntries(
    Object.entries(variables).map(([key, value]) => {
      // フィールド名に機密キーワードが含まれる場合はマスク
      if (SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field))) {
        return [key, '[REDACTED]'];
      }
      // ネストされたオブジェクトも再帰的にサニタイズ
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return [key, sanitizeVariables(value as Record<string, unknown>)];
      }
      return [key, value];
    }),
  );
};

// 初期化時にキャッシュ（パフォーマンス最適化）
const sentryEnabled = isSentryEnabled();

export const sentryLink = onError(({ graphQLErrors, networkError, operation }) => {
  // Sentryが無効な場合はスキップ（開発環境でのTransport disabledエラーを防止）
  if (!sentryEnabled) {
    return;
  }

  if (graphQLErrors) {
    // biome-ignore lint/complexity/noForEach: <explanation>
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      if (
        isExpectedAuthGraphqlError(message, operation.operationName, extensions as Record<string, unknown> | undefined)
      ) {
        return;
      }

      const errorMessage = `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`;

      // Sentryにエラーを送信（変数をサニタイズ）
      Sentry.captureException(new Error(errorMessage), {
        contexts: {
          graphql: {
            operationName: operation.operationName,
            variables: sanitizeVariables(operation.variables),
            extensions,
          },
        },
        tags: {
          errorType: 'graphql',
          operationName: operation.operationName,
        },
        level: 'error',
      });
    });
  }

  if (networkError) {
    if (isExpectedAuthNetworkError(networkError.message)) {
      return;
    }

    // ネットワークエラーをSentryに送信（変数をサニタイズ）
    Sentry.captureException(networkError, {
      contexts: {
        graphql: {
          operationName: operation.operationName,
          variables: sanitizeVariables(operation.variables),
        },
      },
      tags: {
        errorType: 'network',
        operationName: operation.operationName,
      },
      level: 'error',
    });
  }
});
